import React, { useState, useEffect, useRef } from "react";
import "../../styles/Blog.css";
import { getBlogCategories, getBlogs } from "../../api/contentApi";
import { subscribeToNewsletter } from "../../api/newsletterApi";
import CustomYouTubePlayer from "../learning/CustomYouTubePlayer";
import ApiContentState from "../common/ApiContentState";

export default function Blog() {
 
  const [activeCategory, setActiveCategory] = useState("All Categories");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("Latest");
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [toastMessage, setToastMessage] = useState(null);
  const [apiArticles, setApiArticles] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [articlesLoadError, setArticlesLoadError] = useState("");


  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);


  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const tipsList = [
    "Pro Tip: Retention drops most in the first 30 seconds. Keep intros under 5 seconds!",
    "Pro Tip: Use high-contrast thumbnails with 3 words or less for higher CTR.",
    "Pro Tip: Repurpose long YouTube videos into 3 Instagram Reels to double your reach."
  ];


  const [showOnlySaved, setShowOnlySaved] = useState(false);


  const [activeArticleModal, setActiveArticleModal] = useState(null);
  const [readerDarkMode, setReaderDarkMode] = useState(false);
  const [readerFontSize, setReaderFontSize] = useState(16); // Default 16px
  const [readingProgress, setReaderReadingProgress] = useState(0);


  const articlesTopRef = useRef(null);
  const readerBodyRef = useRef(null);


  const ARTICLES_PER_PAGE = 6;


  const [bookmarkedIds, setBookmarkedIds] = useState(() => {
    const saved = localStorage.getItem("cd_bookmarked_blogs");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("cd_bookmarked_blogs", JSON.stringify(bookmarkedIds));
  }, [bookmarkedIds]);

 
  useEffect(() => {
    if (activeArticleModal || isVideoModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setReaderReadingProgress(0); 
    }
    return () => { document.body.style.overflow = ""; };
  }, [activeArticleModal, isVideoModalOpen]);


  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchQuery, showOnlySaved]);

  useEffect(() => {
    let cancelled = false;

    setIsLoadingArticles(true);
    setArticlesLoadError("");
    getBlogs({
      search: searchQuery,
      category: activeCategory === "All Categories" ? "" : activeCategory,
      sort: sortBy,
    })
      .then((items) => {
        if (!cancelled) {
          setApiArticles(Array.isArray(items) ? items.map((item) => ({
            id: item.id,
            title: item.title,
            summary: item.description || '',
            category: item.category || item.platform || 'Content Creation',
            isTrending: Boolean(item.isTrending),
            level: item.difficulty || 'Beginner',
            image: item.imageUrl,
            author: item.author || 'Creator Dost Team',
            avatar: item.authorAvatar,
            date: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '',
            readTime: item.duration ? `${item.duration} min read` : '5 min read',
            content: item.content || item.description || '',
          })) : []);
        }
      })
      .catch((error) => {
        console.error("Unable to load blogs:", error);
        if (!cancelled) {
          setApiArticles([]);
          setArticlesLoadError("Unable to load articles. Please try again.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingArticles(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeCategory, searchQuery, sortBy]);

  useEffect(() => {
    getBlogCategories()
      .then((items) => setApiCategories(Array.isArray(items) ? items : []))
      .catch((error) => console.error("Unable to load blog categories:", error));
  }, []);

  const handleReaderScroll = () => {
    if (readerBodyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = readerBodyRef.current;
      const totalScroll = scrollHeight - clientHeight;
      if (totalScroll > 0) {
        const progress = (scrollTop / totalScroll) * 100;
        setReaderReadingProgress(progress);
      }
    }
  };

  const categoryIcons = {
    YouTube: "▶",
    Instagram: "📷",
    Marketing: "📢",
    Monetization: "💰",
  };

  const categories = [
    { label: "All Categories", icon: "▦" },
    ...apiCategories.map((category) => ({
      label: category,
      icon: categoryIcons[category] || "•",
    })),
  ];

  const popularTopics = categories.slice(1).map((category) => ({
    name: category.label,
    count: "Articles",
    icon: category.icon,
    color: "#eef4ff",
    categoryKey: category.label,
  }));



  const articles = apiArticles || [];

  const topReads = articles.slice(0, 3).map((article, index) => ({
    id: article.id,
    rank: index + 1,
    title: article.title,
    date: article.date,
    image: article.image,
    articleRef: article,
  }));


  const filteredArticles = showOnlySaved
    ? articles.filter((art) => bookmarkedIds.includes(art.id))
    : articles;


  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
  const paginatedArticles = filteredArticles.slice(startIndex, startIndex + ARTICLES_PER_PAGE);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (articlesTopRef.current) {
      articlesTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toggleBookmark = (e, id) => {
    e.stopPropagation();
    if (bookmarkedIds.includes(id)) {
      setBookmarkedIds(bookmarkedIds.filter((bId) => bId !== id));
      triggerToast("Removed from Bookmarks");
    } else {
      setBookmarkedIds([...bookmarkedIds, id]);
      triggerToast("Saved to Bookmarks 🔖");
    }
  };

  const triggerToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (newsletterEmail) {
      try {
        const response = await subscribeToNewsletter(newsletterEmail);
        triggerToast(`🎉 ${response.message}`);
      } catch (error) {
        triggerToast(error.message);
        return;
      }
      setNewsletterEmail("");
    }
  };

  const shareArticle = (platform, title) => {
    const url = window.location.href;
    if (platform === "copy") {
      navigator.clipboard.writeText(url);
      triggerToast("Link copied to clipboard! 📋");
      return;
    }
    const links = {
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(title + " " + url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };
    if (links[platform]) window.open(links[platform], "_blank");
  };

  return (
    <div className="blog-page-wrapper">
      
  
      {toastMessage && (
        <div className="blog-toast-notification">
          <span>{toastMessage}</span>
        </div>
      )}

  
      <section className="blog-hero-section">
        <div className="blog-max-width blog-hero-container">
          <div className="blog-hero-left">
            <span className="blog-tagline">OUR BLOG</span>
            <h1 className="blog-hero-title">
              Insights. Strategies. <br />
              <span className="blog-text-blue-gradient">Creator Growth.</span>
            </h1>
            <p className="blog-hero-subtitle">
              Actionable tips, in-depth guides and expert insights to help you learn, grow and build a successful creator career.
            </p>

            <div className="blog-search-box">
              <input
                type="text"
                placeholder="Search blogs by title, topic, or author..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="blog-search-btn" aria-label="Search">🔍</button>
            </div>
          </div>

          <div className="blog-hero-right">
            <div className="blog-illustration-card">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
                alt="Creator working on laptop"
                className="blog-hero-img"
              />
              
             
              <button 
                className="blog-floating-play-btn" 
                onClick={() => setIsVideoModalOpen(true)}
                title="Watch Creators Dost Intro Video"
                aria-label="Play Video"
              >
                <div className="play-icon-box">
                  <span className="play-pulse-ring"></span>
                  <span className="play-pulse-ring ring-two"></span>
                  <span className="play-triangle">▶</span>
                </div>
                <span className="play-btn-text">Watch Intro</span>
              </button>

          
              <div 
                className="blog-badge-float top-right interactive-tooltip-trigger"
                onClick={() => setCurrentTipIndex((prev) => (prev + 1) % tipsList.length)}
                title="Click for a Creator Tip!"
              >
                <span>💡 Tip</span>
                <div className="tooltip-popover-card">
                  <p>{tipsList[currentTipIndex]}</p>
                  <span className="tooltip-next-hint">Click icon for next tip 🔄</span>
                </div>
              </div>

              
              <div 
                className="blog-badge-float bottom-right interactive-tooltip-trigger"
                onClick={() => { setActiveCategory("Growth"); setShowOnlySaved(false); }}
                title="Click to view Growth Articles"
              >
                <span>📈 Growth</span>
                <div className="tooltip-popover-card">
                  <p>🚀 10,000+ Creators grew their reach this month!</p>
                  <span className="tooltip-next-hint">Click to see Growth Guides ➔</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      
      <section className="blog-max-width blog-categories-bar">
        <div className="blog-categories-scroll">
          
          
          <button
            className={`blog-category-pill saved-pill ${showOnlySaved ? "active-saved" : ""}`}
            onClick={() => setShowOnlySaved(!showOnlySaved)}
          >
            <span className="pill-icon">🔖</span>
            <span>Saved ({bookmarkedIds.length})</span>
          </button>

          {!showOnlySaved && categories.map((cat) => (
            <button
              key={cat.label}
              className={`blog-category-pill ${activeCategory === cat.label ? "active" : ""}`}
              onClick={() => setActiveCategory(cat.label)}
            >
              <span className="pill-icon">{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}

        </div>
      </section>

      
      <section className="blog-max-width blog-main-layout" ref={articlesTopRef}>
        
        
        <div className="blog-content-left">
          <div className="blog-section-header">
            <h2>
              {showOnlySaved ? "Saved Articles" : activeCategory === "All Categories" ? "Latest Articles" : `${activeCategory} Articles`}
              <span className="blog-count-badge">({filteredArticles.length})</span>
            </h2>
            
            
            <div className="blog-custom-sort-wrapper">
              <span className="blog-sort-label">Sort by:</span>
              <div className="blog-sort-dropdown-container">
                <button 
                  type="button"
                  className={`blog-sort-trigger-btn ${isSortOpen ? "active" : ""}`}
                  onClick={() => setIsSortOpen(!isSortOpen)}
                >
                  <span>{sortBy}</span>
                  <span className={`blog-sort-chevron ${isSortOpen ? "rotate" : ""}`}>▾</span>
                </button>

                {isSortOpen && (
                  <>
                    <div className="blog-dropdown-backdrop" onClick={() => setIsSortOpen(false)} />
                    <ul className="blog-sort-menu-list">
                      <li 
                        className={`blog-sort-item ${sortBy === "Latest" ? "selected" : ""}`}
                        onClick={() => { setSortBy("Latest"); setIsSortOpen(false); }}
                      >
                        Latest
                        {sortBy === "Latest" && <span className="blog-check-icon">✓</span>}
                      </li>
                      <li 
                        className={`blog-sort-item ${sortBy === "Popular" ? "selected" : ""}`}
                        onClick={() => { setSortBy("Popular"); setIsSortOpen(false); }}
                      >
                        Most Popular
                        {sortBy === "Popular" && <span className="blog-check-icon">✓</span>}
                      </li>
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>

          
          {isLoadingArticles || articlesLoadError ? (
            <ApiContentState
              loading={isLoadingArticles}
              error={articlesLoadError}
              emptyMessage=""
            />
          ) : filteredArticles.length === 0 ? (
            <div className="blog-empty-state">
              <div className="empty-icon">{showOnlySaved ? "🔖" : "🔍"}</div>
              <h3>{showOnlySaved ? "No Saved Articles" : "No articles found"}</h3>
              <p>
                {showOnlySaved 
                  ? "You haven't saved any articles yet. Click the bookmark icon (📑) on any article card to save it for later." 
                  : `We couldn't find any articles matching "${searchQuery}". Try searching another keyword or reset filters.`
                }
              </p>
              <button 
                className="blog-reset-btn" 
                onClick={() => { setSearchQuery(""); setActiveCategory("All Categories"); setShowOnlySaved(false); }}
              >
                Reset Filters ↺
              </button>
            </div>
          ) : (
            <div className="blog-articles-grid">
              {paginatedArticles.map((article, index) => {
                const isBookmarked = bookmarkedIds.includes(article.id);
                return (
                  <article 
                    key={article.id} 
                    className="blog-article-card"
                    style={{ animationDelay: `${index * 0.08}s` }}
                    onClick={() => setActiveArticleModal(article)}
                  >
                    <div className="blog-card-thumb">
                      <img src={article.image} alt={article.title} />
                      <span className="blog-card-badge">{article.category}</span>
                      
                      {article.isTrending && (
                        <span className="blog-trending-badge">🔥 Trending</span>
                      )}

                      <button 
                        className={`blog-bookmark-btn ${isBookmarked ? "saved" : ""}`}
                        onClick={(e) => toggleBookmark(e, article.id)}
                        title={isBookmarked ? "Remove Bookmark" : "Save Article"}
                      >
                        {isBookmarked ? "🔖" : "📑"}
                      </button>
                    </div>

                    <div className="blog-card-content">
                      <div className="blog-card-meta-row">
                        <span className="level-badge">{article.level}</span>
                        <span className="read-time-tag">⏱️ {article.readTime}</span>
                      </div>

                      <h3 className="blog-card-title">{article.title}</h3>
                      <p className="blog-card-summary">{article.summary}</p>

                      <div className="blog-card-author-row">
                        <img src={article.avatar} alt={article.author} className="author-avatar" />
                        <div className="author-meta">
                          <span className="author-name">{article.author}</span>
                          <span className="article-date-time">{article.date}</span>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          
          {filteredArticles.length > 0 && (
            <div className="blog-pagination">
              <button 
                className="pagination-arrow" 
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                aria-label="Previous Page"
              >
                ‹
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  className={`pagination-num ${currentPage === pageNum ? "active" : ""}`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              ))}

              <button 
                className="pagination-arrow"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                aria-label="Next Page"
              >
                ›
              </button>
            </div>
          )}
        </div>

        
        <aside className="blog-sidebar-right">
          
          {/* WIDGET 1: POPULAR TOPICS */}
          <div className="blog-widget-card">
            <h3 className="widget-title">Popular Topics</h3>
            <ul className="popular-topics-list">
              {popularTopics.map((topic) => (
                <li 
                  key={topic.name} 
                  className="topic-item"
                  onClick={() => { setActiveCategory(topic.categoryKey); setShowOnlySaved(false); }}
                >
                  <div className="topic-icon-box" style={{ backgroundColor: topic.color }}>
                    {topic.icon}
                  </div>
                  <div className="topic-info">
                    <h4>{topic.name}</h4>
                    <p>{topic.count}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          
          {/* <div className="blog-widget-card newsletter-widget">
            <h3>Subscribe to Our Newsletter</h3>
            <p>Get the latest insights, tips and updates delivered to your inbox.</p>
            
            <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
              <input
                type="email"
                placeholder="Enter your email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
              />
              <button type="submit" className="newsletter-btn">Subscribe ➔</button>
            </form>

            <div className="newsletter-graphic">
              <span>📩</span>
            </div>
          </div> */}

          
          <div className="blog-widget-card">
            <h3 className="widget-title">Top Reads</h3>
            <div className="top-reads-list">
              {topReads.map((item) => (
                <div 
                  key={item.id} 
                  className="top-read-item"
                  onClick={() => setActiveArticleModal(item.articleRef)}
                >
                  <div className="top-read-rank">{item.rank}</div>
                  <img src={item.image} alt={item.title} className="top-read-img" />
                  <div className="top-read-info">
                    <h4>{item.title}</h4>
                    <p>{item.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </aside>
      </section>

      
      <section className="blog-max-width blog-cta-section">
        <div className="blog-cta-dark-card">
          <div className="blog-cta-left-branding">
            <div className="blog-graduation-icon-box">
              <span>🎓</span>
            </div>
          </div>

          <div className="blog-cta-content">
            <span className="blog-cta-kicker">KEEP LEARNING, KEEP GROWING</span>
            <h2>Your Creator Journey Never Stops</h2>
            <p>Explore our premium courses and take your skills to the next level.</p>
          </div>

          <div className="blog-cta-buttons">
            <button className="btn-yellow" onClick={() => window.location.hash = '#courses'}>
              Explore Courses ➔
            </button>
            <button className="btn-dark-outline" onClick={() => window.location.hash = '#stories'}>
              Join Community
            </button>
          </div>
        </div>
      </section>

      
      {isVideoModalOpen && (
        <div className="blog-video-modal-curtain">
          <div className="blog-modal-backdrop" onClick={() => setIsVideoModalOpen(false)} />
          <div className="blog-video-modal-content">
            <button className="blog-video-close-btn" onClick={() => setIsVideoModalOpen(false)}>✕</button>
            <div className="blog-video-iframe-wrapper">
              <CustomYouTubePlayer
                videoUrl="https://www.youtube.com/embed/-34KeSMV0SI"
                title="Creators Dost Intro"
                autoplay
              />
            </div>
          </div>
        </div>
      )}

      
      {activeArticleModal && (
        <div className="blog-modal-fixed-curtain">
          <div className="blog-modal-backdrop" onClick={() => setActiveArticleModal(null)} />
          
          <div 
            ref={readerBodyRef}
            onScroll={handleReaderScroll}
            className={`blog-reader-modal-body ${readerDarkMode ? "dark-theme" : ""}`}
            style={{ fontSize: `${readerFontSize}px` }}
          >
            {/* READING PROGRESS BAR */}
            <div 
              className="blog-reading-progress-bar" 
              style={{ width: `${readingProgress}%` }} 
            />

            
            <div className="reader-top-controls-bar">
              <div className="reader-customizer-group">
                <button 
                  className="reader-toggle-btn"
                  onClick={() => setReaderDarkMode(!readerDarkMode)}
                  title="Toggle Light/Dark Theme"
                >
                  {readerDarkMode ? "☀️ Light" : "🌙 Dark"}
                </button>

                <button 
                  className="reader-font-btn"
                  onClick={() => setReaderFontSize((prev) => Math.max(prev - 1, 14))}
                  title="Decrease Font Size"
                >
                  A-
                </button>
                <button 
                  className="reader-font-btn"
                  onClick={() => setReaderFontSize((prev) => Math.min(prev + 1, 20))}
                  title="Increase Font Size"
                >
                  A+
                </button>
              </div>

              <button className="blog-reader-close-btn" onClick={() => setActiveArticleModal(null)}>✕</button>
            </div>

            <div className="reader-header">
              <span className="reader-category-tag">{activeArticleModal.category}</span>
              <span className="reader-read-time">⏱️ {activeArticleModal.readTime}</span>
              <h1>{activeArticleModal.title}</h1>

              <div className="reader-author-bar">
                <img src={activeArticleModal.avatar} alt={activeArticleModal.author} />
                <div>
                  <h5>{activeArticleModal.author}</h5>
                  <span>Published on {activeArticleModal.date}</span>
                </div>

                <button 
                  className="reader-bookmark-btn"
                  onClick={(e) => toggleBookmark(e, activeArticleModal.id)}
                >
                  {bookmarkedIds.includes(activeArticleModal.id) ? "🔖 Saved" : "📑 Save Story"}
                </button>
              </div>
            </div>

            <div className="reader-cover-img">
              <img src={activeArticleModal.image} alt={activeArticleModal.title} />
            </div>

            <div 
              className="reader-html-body"
              dangerouslySetInnerHTML={{ __html: activeArticleModal.content }}
            />

            <div className="reader-share-footer">
              <h4>Share this article:</h4>
              <div className="share-buttons">
                <button onClick={() => shareArticle("whatsapp", activeArticleModal.title)}>🟢 WhatsApp</button>
                <button onClick={() => shareArticle("twitter", activeArticleModal.title)}>🐦 Twitter/X</button>
                <button onClick={() => shareArticle("linkedin", activeArticleModal.title)}>💼 LinkedIn</button>
                <button onClick={() => shareArticle("copy", activeArticleModal.title)}>🔗 Copy Link</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}